import { type FirebaseApp, initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, type Auth } from 'firebase/auth';
import {
  connectFirestoreEmulator,
  type Firestore,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import { connectFunctionsEmulator, type Functions, getFunctions } from 'firebase/functions';
import { APP_HOST } from '@shh/shared';

/*
 * No Analytics, no Performance Monitoring: nothing is tracked (DSGVO).
 * Firestore keeps an offline cache (IndexedDB) so the wall display keeps working and
 * queues taps while the WLAN is gone.
 */
const env = import.meta.env;
const projectId = env.VITE_FIREBASE_PROJECT_ID || 'demo-sweethomehub';
/** "demo-*" projects only exist in the emulator (Firebase convention) – also the default without .env. */
export const useEmulators = env.VITE_USE_EMULATORS === 'true' || projectId.startsWith('demo-');

let services: { app: FirebaseApp; auth: Auth; db: Firestore; functions: Functions } | undefined;

export function firebase() {
  if (services) return services;
  const app = initializeApp({
    apiKey: env.VITE_FIREBASE_API_KEY || 'demo-key',
    // On our own domain, sign-in runs on the same address as the app: Safari and others block
    // the third-party storage a firebaseapp.com helper page would need. Previews keep the default.
    authDomain: window.location.hostname === APP_HOST ? APP_HOST : env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  });
  const auth = getAuth(app);
  // Sign-in mails in German (the template's language follows this code)
  auth.languageCode = 'de';
  const db = initializeFirestore(app, {
    localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
  });
  const functions = getFunctions(app, 'europe-west3');

  if (useEmulators) {
    // Ports are configurable so the E2E suite can run its own emulators next to yours.
    const port = (v: string | undefined, fallback: number) => Number(v) || fallback;
    connectAuthEmulator(auth, `http://127.0.0.1:${port(env.VITE_AUTH_EMULATOR_PORT, 9099)}`, {
      disableWarnings: true,
    });
    connectFirestoreEmulator(db, '127.0.0.1', port(env.VITE_FIRESTORE_EMULATOR_PORT, 8080));
    connectFunctionsEmulator(functions, '127.0.0.1', port(env.VITE_FUNCTIONS_EMULATOR_PORT, 5001));
  }
  services = { app, auth, db, functions };
  return services;
}
